import {
  STATUS_PAGE_IMPACT_SEVERITY,
  StatusPageBannerConfig,
  StatusPageBannerItem,
  StatusPageComponent,
  StatusPageIncident,
  StatusPageScheduledMaintenance,
  StatusPageSummaryResponse,
} from './types';

const MAINTENANCE_IN_PROGRESS_STATUSES = new Set(['in_progress', 'verifying']);

const MAINTENANCE_UPCOMING_STATUSES = new Set(['scheduled', 'confirmed']);

/** Hours before scheduled maintenance when the Statuspage embed starts surfacing it. */
const UPCOMING_MAINTENANCE_LEAD_TIME_MS = 24 * 60 * 60 * 1000;

function getImpactSeverity(impact: StatusPageIncident['impact']): number {
  return STATUS_PAGE_IMPACT_SEVERITY[impact] ?? STATUS_PAGE_IMPACT_SEVERITY.none;
}

function incidentAffectsComponent(incident: StatusPageIncident, componentId: string): boolean {
  return incident.components.some((component) => component.id === componentId);
}

function maintenanceAffectsComponent(maintenance: StatusPageScheduledMaintenance, componentId: string): boolean {
  return maintenance.components.some((component) => component.id === componentId);
}

function itemAffectsAnyComponent(
  item: StatusPageIncident | StatusPageScheduledMaintenance,
  componentIds: string[]
): boolean {
  return componentIds.some((componentId) => {
    if ('scheduled_for' in item) {
      return maintenanceAffectsComponent(item, componentId);
    }
    return incidentAffectsComponent(item, componentId);
  });
}

function isVisibleMaintenance(maintenance: StatusPageScheduledMaintenance, nowMs: number): boolean {
  if (MAINTENANCE_IN_PROGRESS_STATUSES.has(maintenance.status)) {
    return true;
  }

  if (!MAINTENANCE_UPCOMING_STATUSES.has(maintenance.status)) {
    return false;
  }

  const scheduledForMs = Date.parse(maintenance.scheduled_for);
  if (Number.isNaN(scheduledForMs)) {
    return false;
  }

  const msUntilMaintenance = scheduledForMs - nowMs;
  return msUntilMaintenance >= 0 && msUntilMaintenance <= UPCOMING_MAINTENANCE_LEAD_TIME_MS;
}

function toBannerItem(
  type: StatusPageBannerItem['type'],
  item: StatusPageIncident | StatusPageScheduledMaintenance
): StatusPageBannerItem {
  if (type === 'INCIDENT') {
    return { type, item: item as StatusPageIncident };
  }
  return { type, item: item as StatusPageScheduledMaintenance };
}

function compareByImpact(left: StatusPageBannerItem, right: StatusPageBannerItem): number {
  const impactDelta = getImpactSeverity(right.item.impact) - getImpactSeverity(left.item.impact);
  if (impactDelta !== 0) {
    return impactDelta;
  }

  // Prefer active incidents over maintenance when severity matches.
  if (left.type !== right.type) {
    return left.type === 'INCIDENT' ? -1 : 1;
  }

  return 0;
}

/**
 * Returns unresolved incidents and in-window scheduled maintenances, optionally filtered to
 * configured Statuspage component IDs from LaunchDarkly targeting.
 */
export function selectVisibleStatusPageItems(
  summary: StatusPageSummaryResponse,
  config: StatusPageBannerConfig | null | undefined,
  nowMs = Date.now()
): StatusPageBannerItem[] {
  const rawComponentIds = config?.componentIds;
  const componentIds = Array.isArray(rawComponentIds)
    ? rawComponentIds.filter(
        (componentId): componentId is string => typeof componentId === 'string' && componentId.length > 0
      )
    : [];

  const incidents = summary.incidents.filter((incident) => {
    if (componentIds.length === 0) {
      return true;
    }
    return itemAffectsAnyComponent(incident, componentIds);
  });

  const maintenances = summary.scheduled_maintenances.filter((maintenance) => {
    if (!isVisibleMaintenance(maintenance, nowMs)) {
      return false;
    }
    if (componentIds.length === 0) {
      return true;
    }
    return itemAffectsAnyComponent(maintenance, componentIds);
  });

  return [
    ...incidents.map((incident) => toBannerItem('INCIDENT', incident)),
    ...maintenances.map((maintenance) => toBannerItem('MAINTENANCE', maintenance)),
  ].sort(compareByImpact);
}

export function getStatusPageItemComponents(item: StatusPageBannerItem): StatusPageComponent[] {
  return item.item.components;
}
